(function () {
  const TRACK_SELECTOR = '.main-product__media [data-pdp-video-track]';
  const MIN_VISIBLE_RATIO = 0.5;
  const COMPLETE_RATIO = 0.98;
  const MIN_REPORT_MILLISECONDS = 300;

  function pushToDataLayer(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      eventModel: {
         ...payload
      }
    });
  }

  function formatSeconds(milliseconds) {
    return Number((milliseconds / 1000).toFixed(1));
  }

  function getRoundedDurationSeconds(video) {
    return Math.round(video?.duration || 0);
  }

  function getTrackVideo(trackNode) {
    if (!trackNode) return null;
    if (trackNode.tagName === 'VIDEO') return trackNode;
    return trackNode.querySelector('video');
  }

  function getCurrentItemId(trackNode) {
    const productRoot = trackNode.closest('[data-main-product]') || document;

    return (
      productRoot.querySelector('.product-form .product-variant-id')?.value ||
      productRoot.querySelector('input[data-good-id]')?.value ||
      ''
    );
  }

  class PDPVideoGaTracker {
    constructor(trackNode) {
      this.trackNode = trackNode;
      this.video = getTrackVideo(trackNode);
      this.visibleRatio = 0;
      this.startedAt = null;
      this.visiblePlayMilliseconds = 0;
      this.completed = false;

      if (!this.video) return;

      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleMediaEvent = this.handleMediaEvent.bind(this);
      this.handlePageHide = this.handlePageHide.bind(this);
      this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
      this.evaluateState = this.evaluateState.bind(this);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.handleIntersection, {
          threshold: [0, 0.25, 0.5, 0.75, 1]
        });
      } else {
        this.observer = null;
        this.visibleRatio = this.getVisibleRatioFallback();
      }

      this.bindEvents();

      if (this.observer) {
        this.observer.observe(this.trackNode);
      }

      this.evaluateState('init');
    }

    bindEvents() {
      ['play', 'pause', 'waiting', 'seeking', 'seeked', 'stalled', 'emptied', 'timeupdate', 'ended'].forEach((eventName) => {
        this.video.addEventListener(eventName, this.handleMediaEvent);
      });

      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('pagehide', this.handlePageHide);
      window.addEventListener('beforeunload', this.handleBeforeUnload);

      if (!this.observer) {
        window.addEventListener('scroll', this.handleViewportChange, { passive: true });
        window.addEventListener('resize', this.handleViewportChange);
      }
    }

    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.target !== this.trackNode) return;
        this.visibleRatio = entry.intersectionRatio || 0;
      });

      this.evaluateState('viewport_change');
    }

    handleVisibilityChange() {
      this.evaluateState(document.visibilityState === 'visible' ? 'page_visible' : 'page_hidden');
    }

    handleViewportChange() {
      this.visibleRatio = this.getVisibleRatioFallback();
      this.evaluateState('viewport_change');
    }

    handleMediaEvent(event) {
      this.evaluateState(event?.type);
    }

    handlePageHide() {
      this.evaluateState('page_hide');
    }

    handleBeforeUnload() {
      this.evaluateState('before_unload');
    }

    isInViewport() {
      return this.visibleRatio >= MIN_VISIBLE_RATIO && document.visibilityState === 'visible';
    }

    isPlaying() {
      const minReadyState = typeof HTMLMediaElement !== 'undefined' ? HTMLMediaElement.HAVE_CURRENT_DATA : 2;

      return Boolean(this.video) && !this.video.paused && !this.video.ended && !this.video.seeking && this.video.readyState >= minReadyState;
    }

    startTimer() {
      if (this.startedAt !== null) return;
      this.startedAt = Date.now();
    }

    stopTimer() {
      if (this.startedAt === null) return 0;

      const segmentMilliseconds = Date.now() - this.startedAt;
      this.visiblePlayMilliseconds += segmentMilliseconds;
      this.startedAt = null;

      return segmentMilliseconds;
    }

    getVisibleRatioFallback() {
      const rect = this.trackNode.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      if (!rect.width || !rect.height) return 0;

      const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));

      return (visibleWidth * visibleHeight) / (rect.width * rect.height);
    }

    hasCompleted() {
      if (!this.video || !isFinite(this.video.duration) || this.video.duration <= 0) {
        return false;
      }

      return this.video.currentTime / this.video.duration >= COMPLETE_RATIO;
    }

    buildPayload(extraPayload) {
      return {
        item_id: getCurrentItemId(this.trackNode),
        video_id: this.trackNode.dataset.videoId || '',
        video_title: this.trackNode.dataset.videoTitle || '',
        visible_play_seconds: formatSeconds(this.visiblePlayMilliseconds),
        video_duration_seconds: getRoundedDurationSeconds(this.video),
        ...extraPayload
      };
    }

    getStopReason(reason) {
      if (reason === 'page_hide' || reason === 'before_unload') {
        return reason;
      }

      if (document.visibilityState !== 'visible') {
        return 'page_hidden';
      }

      if (!this.isInViewport()) {
        return 'out_of_view';
      }

      if (reason === 'pause' || this.video.paused) {
        return 'pause';
      }

      if (reason === 'waiting' || reason === 'stalled' || reason === 'emptied') {
        return 'buffering';
      }

      if (reason === 'seeking' || this.video.seeking) {
        return 'seeking';
      }

      return reason || 'interrupt';
    }

    reportVisibleTime(reason, segmentMilliseconds) {
      if (segmentMilliseconds < MIN_REPORT_MILLISECONDS) return;

      pushToDataLayer('pdp_video_visible_time', this.buildPayload({
        report_reason: this.getStopReason(reason),
        segment_play_seconds: formatSeconds(segmentMilliseconds)
      }));
    }

    reportComplete(segmentMilliseconds = 0) {
      if (this.completed) return;

      const completedSegmentMilliseconds = segmentMilliseconds || this.stopTimer();
      this.completed = true;

      pushToDataLayer('pdp_video_complete', this.buildPayload({
        segment_play_seconds: formatSeconds(completedSegmentMilliseconds)
      }));
    }

    evaluateState(reason) {
      if (!this.video || this.completed) return;

      const wasTracking = this.startedAt !== null;
      const shouldTrack = this.isInViewport() && this.isPlaying();

      if (shouldTrack) {
        this.startTimer();
      } else if (wasTracking) {
        const segmentMilliseconds = this.stopTimer();

        if (this.isInViewport() && this.hasCompleted()) {
          this.reportComplete(segmentMilliseconds);
          return;
        }

        this.reportVisibleTime(reason, segmentMilliseconds);
      }

      if (shouldTrack && this.isInViewport() && this.hasCompleted()) {
        this.reportComplete();
      }
    }
  }

  function initPdpVideoTrackers(root) {
    if (root instanceof HTMLElement && root.matches?.(TRACK_SELECTOR)) {
      if (!root.__pdpVideoGaTrackerBound) {
        root.__pdpVideoGaTrackerBound = true;
        root.__pdpVideoGaTracker = new PDPVideoGaTracker(root);
      }
    }

    root.querySelectorAll?.(TRACK_SELECTOR).forEach((trackNode) => {
      if (trackNode.__pdpVideoGaTrackerBound) return;

      trackNode.__pdpVideoGaTrackerBound = true;
      trackNode.__pdpVideoGaTracker = new PDPVideoGaTracker(trackNode);
    });
  }

  function boot() {
    initPdpVideoTrackers(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          initPdpVideoTrackers(node);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
